import { inject } from "mobx-react";
import { useCallback, useEffect, useState } from "react";

import { isPWSSessionBackend, parsePWSError, COAST_MESSAGES } from "@inferno/renderer-shared-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { Spinner } from "../../../../../core/ui";
import { useCoastAlerts } from "../common/coast-alert/CoastAlerts.hook";
import { CoastAlertsDisplay } from "../common/coast-alert/CoastAlertsDisplay.components";
import type { Store } from "@inferno/renderer-shared-core";

const log = ILog.logger("Coast Email Update Complete Component");

interface CoastEmailUpdateCompleteProps {
  emailToken?: string;
  store?: Store;
}

export const CoastEmailUpdateComplete = inject("store")(({ emailToken, store }: CoastEmailUpdateCompleteProps) => {
  const { alerts, setErrorAlert, clearAllAlerts, setSuccessAlert } = useCoastAlerts();
  const [isProcessing, setIsProcessing] = useState(false);

  const session = store?.session?.currentSession;

  if (!isPWSSessionBackend(session)) {
    return null;
  }

  const completeUpdate = useCallback(
    async token => {
      clearAllAlerts();
      setIsProcessing(true);

      try {
        const { user: profile } = await session.memberService.completeEmailUpdate(token);
        session.sessionUpdated({ profile });
        setSuccessAlert();
      } catch (e) {
        log.error(e);
        setErrorAlert(parsePWSError(e));
      } finally {
        setIsProcessing(false);
      }
    },
    [clearAllAlerts, session, setErrorAlert, setSuccessAlert],
  );

  useEffect(() => {
    if (emailToken) {
      completeUpdate(emailToken);
    } else {
      setErrorAlert(COAST_MESSAGES.INVALID_LINK);
    }
  }, [completeUpdate, emailToken, setErrorAlert]);

  return (
    <section className="component-email-update-complete">
      <CoastAlertsDisplay alerts={alerts} />
      <Spinner visible={isProcessing} />
    </section>
  );
});
